import { Button } from '@readcloud/component-library';
import { ButtonGroup } from '@readcloud/ui';
import { Tooltip, TooltipTrigger, TooltipContent } from '@readcloud/ui';

import { Popover, PopoverContent, PopoverTrigger } from '@readcloud/ui';
import useMediaQuery from '@readcloud/common/lib/useMediaQuery';
import React from 'react';
import { Icon } from 'react-icons-kit';
import { iosCopy } from 'react-icons-kit/ionicons/iosCopy';
// import C from 'classnames';
import { ConfirmDrawing } from '../../Modals/ConfirmDrawing/ConfirmDrawing';

import { FILTERING_ANNOTATIONS } from '@readcloud/common';
// import { useAppDispatch } from '@readcloud/state';
import { TextSelectionContext } from '../../../context/TextSelection';
// import { usePopoverPlacement } from '../../../hooks/usePopoverPlacement';
import { maxDictionaryQueryLength } from '../../../constants';
import { DictionaryPopover } from '../../Modals/Dictionary/DictionaryPopover';

import {
  MdFileCopy,
  MdHearing,
  MdNoteAdd,
  MdOutlineBookmark,
  MdOutlineChromeReaderMode,
  MdStop,
} from 'react-icons/md';
import { DictionaryIcon } from '../../../assets/DictionaryIcon';
import { knownUsers } from '../../../stories/data';

/**
 * Turn some keys of an object into optionals.
 * Use this for example when defining some default props on a function component that passes down props
 */
type PartialKeys<O extends object, K extends keyof O> = Omit<O, K> & {
  [k in K]?: O[k];
};

// const CopyIcon = ({
//   icon = iosCopy,
//   size = 24,
//   ...props
// }: PartialKeys<
//   React.ComponentPropsWithoutRef<typeof Icon>,
//   'icon' | 'size'
// >) => <Icon icon={icon} size={size} {...props} />;

export interface InnerProps {
  open: boolean;
  className?: string;
  boundingRectElement: { getBoundingClientRect: () => DOMRect };
  children: React.ReactNode;
  container: HTMLElement;
  hidden?: boolean;
  positionFixed?: boolean;
}

// const first = (el: React.ReactNode, ...params: any[]) => el;

export const LearningToolbarPopover: React.FC<InnerProps> = (props) => {
  const { children } = props;

  const { top, left, width, height } =
    props.boundingRectElement?.getBoundingClientRect() || {};

  return children ? (
    <Popover defaultOpen open={props.open}>
      <PopoverTrigger asChild>
        <div
          style={{
            position: 'absolute',
            top: top,
            left: left,
            width: width,
            height: height,
          }}
        ></div>
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        portalled={false}
        className="rc-z-[220] rc-p-0 rc-border-0 rc-w-auto"
        collisionPadding={32}
        side="top"
        sideOffset={16}
      >
        {children}
      </PopoverContent>
    </Popover>
  ) : null;
};

export interface Props {
  outerContainer?: HTMLElement;
  innerRef?: (el: HTMLDivElement) => void;
  onNoteClick?: () => void;
  onCopyClick?: () => void;
  onBookmarkClick?: () => void;
  onDictionaryClick?: () => void;
  onInfoClick?: () => void;
  onListenClick?: () => void;
  onListenStopClick?: () => void;
  listening?: boolean;
  disabledDictionaryTooltip?: string;
  disabledDictionary?: boolean;
  disabledInfoTooltip?: string;
  disabledInfo?: boolean;
  disableCopy?: boolean;
  currentlyFilteringText?: string | undefined;
  saveAnnotationFilter?: (annoFilter: string) => void;
  currentAuthor?: string;
  filterUsers?: string[];
  setUserFilter?: (users: string[]) => void;
  filterAnnotationBy?: (filter: string, users: string[]) => void;
  openConfirmation?: boolean | undefined;
  setConfirmation?: (openDialog: boolean) => void;
  onImmersiveClick?: () => void;
  disableImmersive?: boolean;
  isOnline?: boolean;
  disableTTS?: boolean;
}

export const LearningToolbarActions: React.FunctionComponent<Props> = ({
  onNoteClick,
  onBookmarkClick,
  onDictionaryClick,
  disabledDictionary,
  // disabledDictionaryTooltip,
  disabledInfo,
  // disabledInfoTooltip,
  onInfoClick,
  onCopyClick,
  onListenClick,
  disableCopy,
  disableTTS,
  listening,
  onListenStopClick,
  currentlyFilteringText,
  saveAnnotationFilter,
  openConfirmation,
  setConfirmation,
  currentAuthor,
  filterUsers,
  setUserFilter,
  filterAnnotationBy,
  onImmersiveClick,
  disableImmersive,
  isOnline,
  // outerContainer,
}) => {
  const container = React.useRef();

  const addAnnotationButton = React.useRef(null);
  const [showAddAnnotationTooltip, setShowAddAnnotationTooltip] =
    React.useState<boolean>(false);
  const addBookmarkButton = React.useRef();
  const [showAddBookmarkTooltip, setShowAddBookmarkTooltip] =
    React.useState<boolean>(false);
  const dictionaryButton = React.useRef();
  const [showDictionaryTooltip, setShowDictionaryTooltip] =
    React.useState<boolean>(false);
  const searchButton = React.useRef();
  const [showSearchTooltip, setShowSearchTooltip] =
    React.useState<boolean>(false);
  const copyButton = React.useRef();
  const [showCopyTooltip, setShowCopyTooltip] = React.useState<boolean>(false);
  const listenButton = React.useRef();
  const [showListenTooltip, setShowListenTooltip] =
    React.useState<boolean>(false);
  const immersiveButton = React.useRef();
  const [showImmersiveTooltip, setShowImmersiveTooltip] =
    React.useState<boolean>(false);

  // const dispatch = useAppDispatch();

  const { selectionState } = React.useContext(TextSelectionContext);

  const isLargeScreen = useMediaQuery('(min-width: 768px)');

  return (
    <>
      {saveAnnotationFilter && openConfirmation && setConfirmation ? (
        <ConfirmDrawing
          openDialog={openConfirmation}
          setOpen={setConfirmation}
          setFilter={saveAnnotationFilter}
          annoFilter={currentlyFilteringText || ''}
          confirmAction={onNoteClick}
          currentAuthor={currentAuthor || ''}
          filterUsers={filterUsers || []}
          knownUsers={knownUsers}
          setUserFilter={setUserFilter}
          filterAnnotationBy={filterAnnotationBy}
        />
      ) : null}

      <div
        className="rc-items-center rc-justify-center rc-pointer-events-auto rc-z-[220]"
        data-ignore-toolbar={true}
      >
        <ButtonGroup className="rc-rounded rc-shadow-md rc-pointer-events-auto rc-bg-transparent">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="primary"
                className="rc-h-12"
                onMouseEnter={() => setShowAddAnnotationTooltip(true)}
                onMouseLeave={() => setShowAddAnnotationTooltip(false)}
                onClick={() => {
                  if (
                    currentlyFilteringText &&
                    currentlyFilteringText.indexOf(FILTERING_ANNOTATIONS) >=
                      0 &&
                    !filterUsers.includes(currentAuthor)
                  ) {
                    setConfirmation && setConfirmation(true);
                  } else {
                    onNoteClick && onNoteClick();
                  }
                }}
              >
                <MdNoteAdd size={isLargeScreen ? 24 : 16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Create Annotation</TooltipContent>
          </Tooltip>

          {onBookmarkClick ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="primary"
                  className="rc-h-12"
                  onClick={onBookmarkClick}
                  onMouseEnter={() => setShowAddBookmarkTooltip(true)}
                  onMouseLeave={() => setShowAddBookmarkTooltip(false)}
                >
                  <MdOutlineBookmark size={isLargeScreen ? 24 : 16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Create Bookmark</TooltipContent>
            </Tooltip>
          ) : null}

          {onDictionaryClick ? (
            <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="primary"
                      className="rc-h-12"
                      disabled={disabledDictionary}
                      style={{
                        pointerEvents: !disabledDictionary ? 'auto' : 'none',
                      }}
                      onClick={onDictionaryClick}
                    >
                      <DictionaryIcon height={isLargeScreen ? 24 : 16} />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>Dictionary Lookup</TooltipContent>
              </Tooltip>
              <PopoverContent
                className="rc-bg-background md:rc-max-w-md rc-max-w-xs rc-max-h-full"
                collisionPadding={32}
              >
                <DictionaryPopover
                  query={
                    selectionState?.selectedText?.length <=
                    maxDictionaryQueryLength
                      ? selectionState?.selectedText
                      : undefined
                  }
                />
              </PopoverContent>
            </Popover>
          ) : null}

          {onInfoClick ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="primary"
                  className="rc-h-12"
                  onMouseEnter={() => setShowSearchTooltip(true)}
                  onMouseLeave={() => setShowSearchTooltip(false)}
                  onClick={onInfoClick}
                  disabled={disabledInfo}
                >
                  <i className="fe fe-info"></i>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Search Online</TooltipContent>
            </Tooltip>
          ) : null}

          {!disableCopy ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="primary"
                  className="rc-h-12"
                  onClick={onCopyClick}
                  onMouseEnter={() => setShowCopyTooltip(true)}
                  onMouseLeave={() => setShowCopyTooltip(false)}
                >
                  <MdFileCopy size={isLargeScreen ? 24 : 16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy</TooltipContent>
            </Tooltip>
          ) : null}

          {!disableTTS && onListenClick ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="primary"
                  className="rc-h-12"
                  onMouseEnter={() => setShowListenTooltip(true)}
                  onMouseLeave={() => setShowListenTooltip(false)}
                  onClick={!listening ? onListenClick : onListenStopClick}
                  disabled={!isOnline}
                  style={{ pointerEvents: isOnline ? 'auto' : 'none' }}
                >
                  {listening ? (
                    <MdStop size={isLargeScreen ? 24 : 16} />
                  ) : (
                    <MdHearing size={isLargeScreen ? 24 : 16} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Listen</TooltipContent>
            </Tooltip>
          ) : null}

          {!disableImmersive && onImmersiveClick ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="primary"
                  className="rc-h-12"
                  onMouseEnter={() => setShowImmersiveTooltip(true)}
                  onMouseLeave={() => setShowImmersiveTooltip(false)}
                  onClick={onImmersiveClick}
                  disabled={!isOnline}
                  style={{ pointerEvents: isOnline ? 'auto' : 'none' }}
                >
                  <MdOutlineChromeReaderMode size={isLargeScreen ? 24 : 16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Immersive Reader</TooltipContent>
            </Tooltip>
          ) : null}
        </ButtonGroup>
      </div>
    </>
  );
};
